import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import { LoadingButton } from 'src/components/buttons/LoadingButton';
import FormControl from '@mui/material/FormControl';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { couldBePassword } from 'src/util/credentialFormatValidation';
import { cleanEmail, isValidEmail } from 'src/util/emailUtils';
import type { AuthenticationFlowPageProps } from '../AuthenticationFlow';
import type { RootState } from '../store';
import { setEmail } from '../store/authSlice';
import { useAuth } from 'src/contexts/AuthContext';
import { getUsername } from 'src/util/getUsername';
import { LottieLoader } from 'src/components/LottieLoader';

export type AuthenticationEmailSignInPageProps = AuthenticationFlowPageProps;

export type AuthenticationEmailSignInPageInputs = 'email' | 'password';

export function AuthenticationEmailSignInPage({
  pageId: pageID,
  onPageComplete,
}: AuthenticationEmailSignInPageProps) {
  const { handleSignInWithEmailAndPassword } = useAuth();
  const [forgotPassword, setForgotPassword] = useState({
    emailSent: false,
    clicked: false,
  });
  const startingEmail = useSelector((state: RootState) => {
    return state.email;
  });
  const dispatch = useDispatch();

  const [userCredentials, setUserCredentials] = useState<{
    [K in AuthenticationEmailSignInPageInputs]: string;
  }>({
    email: startingEmail,
    password: '',
  });
  const [errors, setErrors] = useState<{
    [K in AuthenticationEmailSignInPageInputs]: boolean;
  }>({
    email: false,
    password: false,
  });

  const onInputChange = (
    inputName: AuthenticationEmailSignInPageInputs,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setErrors({
      ...errors,
      [inputName]: false,
    });
    setUserCredentials({
      ...userCredentials,
      [inputName]: event.currentTarget.value,
    });
  };

  const handleForgotPassword = async () => {
    setForgotPassword({
      clicked: true,
      emailSent: false,
    });
    const { sendAuthEmail } = await import(
      '../../../firebaseCloud/mail/sendAuthEmail'
    );
    await sendAuthEmail({
      to: startingEmail,
      mode: 'resetPassword',
      username: await getUsername(startingEmail),
    });
    setForgotPassword({
      clicked: true,
      emailSent: true,
    });
  };

  const attemptSubmit = useCallback(async () => {
    const cleanedEmail = cleanEmail(userCredentials.email.toLowerCase());
    const validEmail = isValidEmail(cleanedEmail);
    const validPassword = couldBePassword(userCredentials.password);
    if (!validEmail || !validPassword)
      setErrors({ ...errors, email: !validEmail, password: !validPassword });
    else {
      setErrors({ ...errors, email: false, password: false });
      try {
        const result = await handleSignInWithEmailAndPassword(
          cleanedEmail,
          userCredentials.password,
        );
        if (result) {
          dispatch(setEmail(cleanedEmail));
          onPageComplete(pageID);
        } else {
          setErrors({ ...errors, password: true });
        }
      } catch (e) {
        setErrors({ ...errors, password: true });
        console.warn(e);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCredentials, errors, dispatch, onPageComplete, pageID]);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      if (event.code === 'Enter' || event.code === 'NumpadEnter') {
        event.preventDefault();
        attemptSubmit();
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [attemptSubmit]);

  if (!pageID || !onPageComplete) return null;

  return (
    <Stack spacing={'32px'} my={'64px'} width={'100%'} maxWidth={'420px'}>
      <Stack spacing={'8px'}>
        <Typography
          variant="h4"
          fontWeight="500"
          textTransform={'capitalize'}
          textAlign={'center'}
        >
          Email Sign In
        </Typography>
        <Typography variant="body1" textAlign={'center'}>
          Email found! Enter account password:
        </Typography>
      </Stack>
      <FormControl>
        <Stack textAlign={'center'}>
          <TextField
            error={errors.email}
            helperText={
              errors.email ? 'Please enter a valid email address!' : undefined
            }
            label="Email Address"
            type={'email'}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgb(192, 192, 192)',
              },
            }}
            fullWidth={true}
            name={'email'}
            value={userCredentials.email}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              return onInputChange('email', event);
            }}
            disabled={true}
          />

          <TextField
            error={errors.password}
            helperText={
              errors.password
                ? 'Incorrect password, please try again!'
                : undefined
            }
            label="Password"
            type={'password'}
            InputLabelProps={{ shrink: true }}
            variant="outlined"
            sx={{
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: 'rgb(192, 192, 192)',
              },
              mt: '40px',
            }}
            fullWidth={true}
            name={'password'}
            value={userCredentials.password}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              return onInputChange('password', event);
            }}
            autoFocus
          />
          <Box
            sx={{
              mt: 2,
              textAlign: 'right',
            }}
          >
            {!forgotPassword.clicked ? (
              <Typography
                onClick={handleForgotPassword}
                color="text.secondary"
                sx={{
                  textDecoration: 'underline',
                  cursor: 'pointer',
                }}
                variant="body2"
              >
                Forgot password?
              </Typography>
            ) : !forgotPassword.emailSent ? (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <LottieLoader sx={{ height: '16px', width: '16px' }} />
              </Box>
            ) : (
              <Typography color="text.secondary" variant="body2">
                Email sent! Check your inbox.
              </Typography>
            )}
          </Box>
        </Stack>
      </FormControl>

      <Box textAlign={'center'}>
        <LoadingButton
          variant="contained"
          size="large"
          fullWidth
          onClick={attemptSubmit}
        >
          Sign In
        </LoadingButton>
      </Box>
    </Stack>
  );
}
